/**
 * Store Config Hook using TanStack Query
 * Fetches store configuration with automatic caching
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { StoreConfig, StoreConfigResource } from '../../core/resources/storeConfig';
import { useTagadaContext } from '../providers/TagadaProvider';

import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface UseStoreConfigQueryOptions {
    storeId?: string;
    enabled?: boolean;
}

export interface UseStoreConfigQueryResult {
    // Query data
    storeConfig: StoreConfig | undefined;
    isLoading: boolean;
    error: Error | null;
    isSuccess: boolean;

    // Actions
    refetch: () => Promise<void>;
}

export function useStoreConfigQuery(options: UseStoreConfigQueryOptions = {}): UseStoreConfigQueryResult {
    const { storeId: providedStoreId, enabled = true } = options;
    const { storeId: contextStoreId } = usePluginConfig();
    const { isSessionInitialized } = useTagadaContext();

    // Use provided storeId or fall back to context storeId
    const storeId = providedStoreId || contextStoreId;

    // Create store config resource client
    const storeConfigResource = useMemo(() => {
        try {
            return new StoreConfigResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize store config resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Use TanStack Query for fetching store config
    const {
        data: storeConfig,
        isLoading,
        error,
        isSuccess,
        refetch
    } = useQuery<StoreConfig>({
        queryKey: ['storeConfig', storeId],
        enabled: enabled && !!storeId && isSessionInitialized,
        queryFn: async () => {
            if (!storeId) {
                throw new Error('Store ID not found. Make sure the TagadaProvider is properly configured.');
            }

            return await storeConfigResource.getStoreConfig(storeId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - config doesn't change often
        gcTime: 10 * 60 * 1000, // 10 minutes cache time
    });

    // Refetch function
    const refetchConfig = useCallback(async () => {
        await refetch();
    }, [refetch]);

    return {
        // Query data
        storeConfig,
        isLoading,
        error,
        isSuccess,

        // Actions
        refetch: refetchConfig,
    };
}

